import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from "react-oidc-context";

export const ADMIN_GROUPS = [
  "Flight Range Admins",
  "FlightRangeAdmins",
  "Flight Range Admin",
  "FlightRangeAdmin",
  "Flight_Range_Admins"
];

interface RoleBasedAuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  user: any;
  login: () => void;
  logout: () => void;
}

const RoleBasedAuthContext = createContext<RoleBasedAuthContextType | null>(null);

export const RoleBasedAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) {
      setIsAdmin(false);
      return;
    }

    checkAdminStatus(auth.user);
  }, [auth.isAuthenticated, auth.user]);

  const checkAdminStatus = (user: any) => {
    if (!user || !user.profile) return false;
    
    const possibleClaims = ['groups', 'group', 'roles', 'role', 'memberOf', 'member_of', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/groups'];
    
    for (const claimName of possibleClaims) {
      const groups = user.profile[claimName];
      if (!groups) continue;
      
      if (Array.isArray(groups)) {
        for (const group of groups) {
          if (typeof group === 'string' && 
              ADMIN_GROUPS.some(adminGroup => 
                group.toLowerCase().includes(adminGroup.toLowerCase()))) {
            setIsAdmin(true);
            return;
          }
        }
      } else if (typeof groups === 'string') {
        if (ADMIN_GROUPS.some(adminGroup => 
              groups.toLowerCase().includes(adminGroup.toLowerCase()))) {
          setIsAdmin(true);
          return;
        }
      }
    }
    
    setIsAdmin(false);
  };

  return (
    <RoleBasedAuthContext.Provider value={{
      isAuthenticated: auth.isAuthenticated,
      isAdmin,
      isLoading: auth.isLoading,
      user: auth.user,
      login: auth.signinRedirect,
      logout: auth.removeUser
    }}>
      {children}
    </RoleBasedAuthContext.Provider>
  );
};

export const useRoleBasedAuth = () => {
  const context = useContext(RoleBasedAuthContext);
  if (context === null) {
    throw new Error('useRoleBasedAuth must be used within a RoleBasedAuthProvider');
  }
  return context;
};